import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { Scale } from "lucide-react";

const C = { budget: "#94A3B8", actual: "#2563EB", committed: "#F59E0B", over: "#EF4444", forecast: "#7C3AED" };
const num = (v) => Number(v || 0);
const inr = (v) => `₹${num(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const inrShort = (v) => {
  const n = num(v), a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (a >= 1e3) return `₹${(n / 1e3).toFixed(0)} K`;
  return `₹${n.toFixed(0)}`;
};
const clip = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function Tip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const used = row.budget > 0 ? ((row.actual + row.committed) / row.budget) * 100 : null;
  return (
    <div style={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))", borderRadius: 8, padding: "8px 10px", fontSize: 12, minWidth: 190 }}>
      <p style={{ fontWeight: 600, marginBottom: 4 }}>{row.full || label}</p>
      {[["Budget", row.budget, C.budget], ["Actual", row.actual, C.actual], ["Committed", row.committed, C.committed]].map(([k, v, c]) => (
        <p key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: c, marginRight: 6 }} />{k}</span><span>{inr(v)}</span>
        </p>
      ))}
      <p style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 4, paddingTop: 4, borderTop: "1px solid hsl(var(--border))", fontWeight: 600, color: row.remaining < 0 ? C.over : undefined }}>
        <span>{row.remaining < 0 ? "Over budget by" : "Remaining"}</span><span>{inr(Math.abs(row.remaining))}</span>
      </p>
      {used !== null && <p style={{ opacity: 0.7 }}>{used.toFixed(1)}% of budget used</p>}
    </div>
  );
}

export default function BudgetVsActualChart({ projectId, summary }) {
  const [rows, setRows] = useState(null);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    let live = true;
    api.get(`/projects/${projectId}/budget-vs-actual`)
      .then(({ data }) => { if (live) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [projectId]);

  const all = useMemo(() => (rows || []).map((r) => {
    const budget = num(r.budget), actual = num(r.actual), committed = num(r.committed);
    const full = `${r.code ? `${r.code} · ` : ""}${r.name || "—"}`;
    return { full, label: r.code || clip(r.name || "—", 14), code: r.code || "~", budget, actual, committed, remaining: budget - actual - committed };
  }).filter((r) => r.budget || r.actual || r.committed), [rows]);

  const totals = useMemo(() => all.reduce((t, r) => ({ budget: t.budget + r.budget, actual: t.actual + r.actual, committed: t.committed + r.committed }), { budget: 0, actual: 0, committed: 0 }), [all]);
  const byActivity = useMemo(() => {
    const top = [...all].sort((a, b) => b.budget - a.budget || b.actual - a.actual);
    const picked = limit ? top.slice(0, limit) : top;
    return picked.sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }));
  }, [all, limit]);

  if (rows === null) return <div className="rounded-2xl border border-border/70 bg-card shadow-sm p-4 text-sm text-muted-foreground" data-testid="bva-chart-loading">Loading budget vs actual…</div>;

  const budget = summary ? num(summary.budget) : totals.budget;
  const actual = summary ? num(summary.actual) : totals.actual;
  const committed = summary ? num(summary.committed) : totals.committed;
  const forecast = summary && summary.forecast_final_cost !== undefined ? num(summary.forecast_final_cost) : null;
  const remaining = budget - actual - committed;
  const usedPct = budget > 0 ? ((actual + committed) / budget) * 100 : null;

  if (!all.length) {
    return (
      <div className="rounded-2xl border border-border/70 bg-card shadow-sm p-4" data-testid="bva-chart-empty">
        <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/10 text-blue-500"><Scale className="h-4 w-4" /></span><p className="text-base font-semibold text-foreground">Budget vs actual</p></div>
        <p className="mt-2 text-sm text-muted-foreground">No budget or cost yet. Approve an estimate in Estimates &amp; Budgets, then costs booked to the project will appear here.</p>
      </div>
    );
  }

  const overall = [{ name: "Budget", value: budget, fill: C.budget }, { name: "Actual", value: actual, fill: C.actual }, { name: "Committed", value: committed, fill: C.committed }];
  if (forecast !== null) overall.push({ name: "Forecast", value: forecast, fill: forecast > budget && budget > 0 ? C.over : C.forecast });
  const overCount = all.filter((r) => r.budget > 0 && r.actual + r.committed > r.budget).length;

  return (
    <div className="rounded-2xl border border-border/70 bg-card shadow-sm" data-testid="bva-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
        <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/10 text-blue-500"><Scale className="h-4 w-4" /></span><p className="text-base font-semibold text-foreground">Budget vs actual</p></div>
        {overCount > 0 && <p className="text-xs font-semibold text-rose-500">{overCount} {overCount === 1 ? "activity" : "activities"} over budget</p>}
        {usedPct !== null && (
          <p className={`text-xs font-semibold ${usedPct > 100 ? "text-rose-500" : usedPct > 90 ? "text-amber-500" : "text-emerald-600 dark:text-emerald-400"}`}>
            {usedPct.toFixed(1)}% of budget used (actual + committed)
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-px border-b border-border bg-border md:grid-cols-4">
        {[["Budget", inr(budget), ""], ["Actual cost", inr(actual), ""], ["Committed", inr(committed), ""], [remaining < 0 ? "Over budget" : "Remaining", inr(Math.abs(remaining)), remaining < 0 ? "text-rose-500" : "text-emerald-600 dark:text-emerald-400"]].map(([k, v, cls]) => (
          <div key={k} className="bg-card p-3"><p className="text-[11px] text-muted-foreground">{k}</p><p className={`font-mono text-sm font-bold ${cls}`}>{v}</p></div>
        ))}
      </div>

      <div className="space-y-6 p-4">
        <div>
          <p className="mb-2 text-sm font-semibold text-foreground">1. Whole project</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={overall} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,.25)" vertical={false} />
                <XAxis dataKey="name" stroke="#64748b" fontSize={11} interval={0} />
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={inrShort} width={84} />
                <Tooltip formatter={(v) => inr(v)} cursor={{ fill: "rgba(100,116,139,.12)" }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>{overall.map((d) => <Cell key={d.name} fill={d.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border-t border-border pt-5">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">2. By activity (largest budgets, in WBS order)</p>
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="h-7 rounded-md border border-border bg-background px-2 text-xs" data-testid="bva-chart-limit">
              <option value={8}>Top 8</option><option value={10}>Top 10</option><option value={20}>Top 20</option><option value={0}>All ({all.length})</option>
            </select>
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byActivity} margin={{ top: 8, right: 12, left: 0, bottom: 4 }} barGap={2} barCategoryGap="18%">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,.25)" vertical={false} />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} interval={0} angle={byActivity.length > 8 ? -30 : 0} textAnchor={byActivity.length > 8 ? "end" : "middle"} height={byActivity.length > 8 ? 54 : 30} />
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={inrShort} width={72} />
                <Tooltip content={<Tip />} cursor={{ fill: "rgba(100,116,139,.12)" }} />
                <Legend verticalAlign="top" align="right" height={30} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="budget" name="Budget" fill={C.budget} radius={[3, 3, 0, 0]} />
                <Bar dataKey="actual" name="Actual" fill={C.actual} radius={[3, 3, 0, 0]} />
                <Bar dataKey="committed" name="Committed" fill={C.committed} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">Hover a column for the full activity name and the remaining amount. Committed = approved orders not yet billed.</p>
        </div>
      </div>
    </div>
  );
}
